import React, { useEffect, useRef, useState } from "react";
import { shuffle } from "../lib/tokiPonaData";
import { speakTokiPona, speechAvailable } from "../lib/tokiPonaSpeech";
import { Icon, Dots, Html, SpeakButton } from "./tokiPonaUi";

/* ============================================================
   o kama sona — practice games (an aside to the lessons)
   Each game is self-contained and stateless across mounts: it
   takes the learner's unlocked vocabulary as props, runs its
   own little loop, and reports XP back via onAward. None of
   them touch hearts or lesson progress — they're just for play.

   Props shared by the scored games:
     words   : [{ key, g, n }]   unlocked vocabulary
     onExit  : () => void        back to the games hub
     onAward : (xp:number) => void
   ============================================================ */

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

/* ---------------- metadata for the hub ---------------- */
// `min` = unlocked words needed; `phrases` games need usable example phrases.
export const GAMES = [
  {
    key: "speed",
    icon: "timer",
    title: "tenpo lili",
    sub: "Speed round",
    desc: "Name as many word meanings as you can before the clock runs out — by sight, or by ear.",
    min: 4,
  },
  {
    key: "memory",
    icon: "cards",
    title: "sona awen",
    sub: "Memory match",
    desc: "Flip the tiles to pair each toki pona word with its meaning.",
    min: 4,
  },
  {
    key: "builder",
    icon: "grid",
    title: "o pali e toki",
    sub: "Sentence builder",
    desc: "Tap the scrambled words into order — mind the li, e and la.",
    phrases: true,
  },
  {
    key: "flash",
    icon: "layers",
    title: "lipu sona",
    sub: "Flashcards",
    desc: "Flip through your unlocked words at your own pace. No score.",
    min: 1,
  },
];

/* ============================================================
   1 · Speed round — 30s of rapid meaning recall
   ============================================================ */
const SPEED_SECONDS = 30;

// `avoid` keeps the round from asking the same word twice in a row — with a
// small early vocabulary that happens often enough to feel broken.
export const makeSpeedQ = (words, avoid) => {
  const pool = words.length > 1 ? words.filter((w) => w.key !== avoid) : words;
  const correct = pick(pool);
  const others = shuffle(words.filter((w) => w.g !== correct.g)).slice(0, 3);
  return {
    word: correct.key,
    correct: correct.g,
    options: shuffle([correct.g, ...others.map((w) => w.g)]),
  };
};

// Session-sticky "ears mode" flag for the speed round.
const EARS_KEY = "tpSpeedEars";

export function SpeedQuiz({ words, onExit, onAward }) {
  const [phase, setPhase] = useState("play"); // play | over
  const [timeLeft, setTimeLeft] = useState(SPEED_SECONDS);
  const [score, setScore] = useState(0);
  const [q, setQ] = useState(() => makeSpeedQ(words));
  const [picked, setPicked] = useState(null);
  // Ears mode: the word is spoken instead of shown. Only offered when the
  // browser can speak; remembered for the session.
  const [ears, setEars] = useState(() => {
    if (!speechAvailable) return false;
    try {
      return sessionStorage.getItem(EARS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const awarded = useRef(false);

  const toggleEars = () =>
    setEars((e) => {
      const next = !e;
      try {
        sessionStorage.setItem(EARS_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });

  // In ears mode each fresh question announces itself.
  useEffect(() => {
    if (ears && phase === "play" && !picked) speakTokiPona(q.word);
  }, [ears, phase, q, picked]);

  useEffect(() => {
    if (phase !== "play") return undefined;
    const t = setInterval(() => {
      setTimeLeft((tl) => (tl <= 1 ? 0 : tl - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [phase]);

  useEffect(() => {
    if (timeLeft === 0 && phase === "play") {
      setPhase("over");
      if (!awarded.current) {
        awarded.current = true;
        onAward(score);
      }
    }
  }, [timeLeft, phase, score, onAward]);

  const choose = (opt) => {
    if (picked) return;
    setPicked(opt);
    if (opt === q.correct) setScore((s) => s + 1);
    // Hear the word as feedback (in ears mode it was just spoken).
    if (!ears && speechAvailable) speakTokiPona(q.word);
    setTimeout(() => {
      setPicked(null);
      setQ(makeSpeedQ(words, q.word));
    }, 360);
  };

  const restart = () => {
    awarded.current = false;
    setScore(0);
    setTimeLeft(SPEED_SECONDS);
    setPicked(null);
    setQ(makeSpeedQ(words));
    setPhase("play");
  };

  if (phase === "over") {
    return (
      <GameDone
        title="tenpo li pini!"
        sub="time's up — nicely done"
        cards={[
          { k: "score", v: score, accent: "var(--laso)" },
          { k: "XP earned", v: `+${score}`, accent: "var(--jelo)" },
        ]}
        onAgain={restart}
        onExit={onExit}
      />
    );
  }

  return (
    <>
      <div className="game-head">
        <div className={`timer ${timeLeft <= 5 ? "low" : ""}`}>
          <Icon name="timer" />
          {timeLeft}s
        </div>
        {speechAvailable && (
          <button
            type="button"
            className={`ears-toggle ${ears ? "on" : ""}`}
            onClick={toggleEars}
            aria-label={
              ears ? "switch to reading mode" : "switch to listening mode"
            }
          >
            <Icon name="speaker" />
            {ears ? "kute" : "lukin"}
          </button>
        )}
        <div className="game-score">
          <Icon name="bolt" />
          {score}
        </div>
      </div>
      <div className="qarea">
        <div className="qkind">
          {ears ? "what do you hear?" : "what does this mean?"}
        </div>
        {ears && !picked ? (
          <button
            type="button"
            className="listen-play"
            onClick={() => speakTokiPona(q.word)}
            aria-label="play it again"
          >
            <Icon name="speaker" />
            <span>hear it again</span>
          </button>
        ) : (
          <div className="qword">{q.word}</div>
        )}
        <div className="choices">
          {q.options.map((opt) => {
            let cls = "choice";
            if (picked) {
              if (opt === q.correct) cls += " right";
              else if (opt === picked) cls += " wrong";
              else cls += " dim";
            }
            return (
              <button key={opt} className={cls} onClick={() => choose(opt)}>
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ============================================================
   2 · Memory match — concentration with up to 6 pairs
   ============================================================ */
const buildMemoryTiles = (words) => {
  const pairs = shuffle(words).slice(0, Math.min(6, words.length));
  const tiles = [];
  pairs.forEach((w, i) => {
    tiles.push({ id: `t${i}`, pair: i, kind: "tp", text: w.key });
    tiles.push({ id: `e${i}`, pair: i, kind: "en", text: w.g });
  });
  return shuffle(tiles);
};

export function MemoryMatch({ words, onExit, onAward }) {
  const [tiles, setTiles] = useState(() => buildMemoryTiles(words));
  const [open, setOpen] = useState([]); // indices currently face-up, unmatched
  const [matched, setMatched] = useState([]); // pair ids solved
  const [moves, setMoves] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const awarded = useRef(false);

  const totalPairs = tiles.length / 2;

  useEffect(() => {
    if (!done && totalPairs && matched.length === totalPairs) {
      setDone(true);
      if (!awarded.current) {
        awarded.current = true;
        onAward(10);
      }
    }
  }, [matched, totalPairs, done, onAward]);

  const tap = (i) => {
    if (busy || open.includes(i) || matched.includes(tiles[i].pair)) return;
    // Word tiles say their name when flipped — pronunciation for free.
    if (speechAvailable && tiles[i].kind === "tp") speakTokiPona(tiles[i].text);
    const next = [...open, i];
    setOpen(next);
    if (next.length === 2) {
      setMoves((m) => m + 1);
      const [a, b] = next;
      if (tiles[a].pair === tiles[b].pair) {
        setBusy(true);
        setTimeout(() => {
          setMatched((m) => [...m, tiles[a].pair]);
          setOpen([]);
          setBusy(false);
        }, 340);
      } else {
        setBusy(true);
        setTimeout(() => {
          setOpen([]);
          setBusy(false);
        }, 760);
      }
    }
  };

  const restart = () => {
    awarded.current = false;
    setTiles(buildMemoryTiles(words));
    setOpen([]);
    setMatched([]);
    setMoves(0);
    setBusy(false);
    setDone(false);
  };

  if (done) {
    return (
      <GameDone
        title="ale li sama!"
        sub="every pair matched"
        cards={[
          { k: "moves", v: moves, accent: "var(--laso)" },
          { k: "XP earned", v: "+10", accent: "var(--jelo)" },
        ]}
        onAgain={restart}
        onExit={onExit}
      />
    );
  }

  return (
    <>
      <div className="game-head">
        <div className="qkind">find the pairs</div>
        <div className="game-score">
          {matched.length}/{totalPairs}
        </div>
      </div>
      <div className="qarea">
        <div className="mem-grid">
          {tiles.map((t, i) => {
            const up = open.includes(i);
            const solved = matched.includes(t.pair);
            const cls = `mem-tile ${t.kind === "tp" ? "tp" : ""} ${
              up ? "up" : ""
            } ${solved ? "matched" : ""}`;
            return (
              <button key={t.id} className={cls} onClick={() => tap(i)}>
                {up || solved ? t.text : <Dots />}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ============================================================
   3 · Sentence builder — reorder scrambled words
   ============================================================ */
// Like the in-lesson build drills, the bank carries one trap tile — a
// particle the sentence does NOT use — so word order alone isn't enough:
// you also have to know which little words belong.
export const initBank = (phrase) => {
  const tokens = phrase.tp.split(" ");
  const traps = ["li", "e", "la"].filter((x) => !tokens.includes(x));
  const trap = traps.length ? pick(traps) : null;
  return shuffle([...tokens, ...(trap ? [trap] : [])]).map((tok, i) => ({
    id: i,
    tok,
  }));
};

const dealDeck = (phrases) =>
  shuffle(phrases).slice(0, Math.min(5, phrases.length));

export function SentenceBuilder({ phrases, onExit, onAward }) {
  const [deck, setDeck] = useState(() => dealDeck(phrases));
  const [idx, setIdx] = useState(0);
  const [bank, setBank] = useState(() => initBank(deck[0]));
  const [answer, setAnswer] = useState([]);
  const [checked, setChecked] = useState(null); // null | "right" | "wrong"
  const [right, setRight] = useState(0);
  const awarded = useRef(false);

  const phrase = deck[idx];

  const toAnswer = (item) => {
    if (checked) return;
    setBank((b) => b.filter((x) => x.id !== item.id));
    setAnswer((a) => [...a, item]);
  };
  const toBank = (item) => {
    if (checked) return;
    setAnswer((a) => a.filter((x) => x.id !== item.id));
    setBank((b) => [...b, item]);
  };

  const check = () => {
    const built = answer.map((x) => x.tok).join(" ");
    // Some phrases carry equally-correct alternate orders (phrase.alt).
    const ok = built === phrase.tp || (phrase.alt || []).includes(built);
    setChecked(ok ? "right" : "wrong");
    if (ok) setRight((r) => r + 1);
    // Hear the sentence as it should sound, right or wrong.
    if (speechAvailable) speakTokiPona(phrase.tp);
  };

  const next = () => {
    const ni = idx + 1;
    if (ni >= deck.length) {
      if (!awarded.current) {
        awarded.current = true;
        onAward(right * 2);
      }
      setIdx(ni); // triggers the done view below
      return;
    }
    setIdx(ni);
    setBank(initBank(deck[ni]));
    setAnswer([]);
    setChecked(null);
  };

  const restart = () => {
    // Deal a fresh hand — "Play again" shouldn't replay the same five
    // sentences in the same order.
    const fresh = dealDeck(phrases);
    awarded.current = false;
    setDeck(fresh);
    setIdx(0);
    setBank(initBank(fresh[0]));
    setAnswer([]);
    setChecked(null);
    setRight(0);
  };

  if (idx >= deck.length) {
    return (
      <GameDone
        title="o pini pona!"
        sub="sentences built"
        cards={[
          { k: "correct", v: `${right}/${deck.length}`, accent: "var(--laso)" },
          { k: "XP earned", v: `+${right * 2}`, accent: "var(--jelo)" },
        ]}
        onAgain={restart}
        onExit={onExit}
      />
    );
  }

  return (
    <>
      <div className="game-head">
        <div className="qkind">build the sentence</div>
        <div className="game-score">
          {idx + 1}/{deck.length}
        </div>
      </div>
      <div className="qarea">
        <div className="build-prompt">“{phrase.en}”</div>
        <div className={`build-answer ${checked || ""}`}>
          {answer.length === 0 && (
            <span className="build-hint">tap words below…</span>
          )}
          {answer.map((item) => (
            <button
              key={item.id}
              className="tok"
              onClick={() => toBank(item)}
            >
              {item.tok}
            </button>
          ))}
        </div>
        <div className="build-bank">
          {bank.map((item) => (
            <button
              key={item.id}
              className="tok ghost"
              onClick={() => toAnswer(item)}
            >
              {item.tok}
            </button>
          ))}
        </div>
        {checked === "wrong" && (
          <Html
            className="notes"
            html={`the order was <b>${phrase.tp}</b>`}
          />
        )}
      </div>
      <div className={`foot ${checked || ""}`}>
        {!checked ? (
          <button
            className="btn"
            disabled={answer.length === 0}
            onClick={check}
          >
            Check
          </button>
        ) : (
          <>
            <div className="verdict">
              <b>{checked === "right" ? "pona!" : "ike a"}</b>
              <small>
                {checked === "right" ? "that's right" : "have another look"}
              </small>
            </div>
            <button
              className={`btn ${checked === "right" ? "" : "loje"}`}
              onClick={next}
            >
              {idx + 1 >= deck.length ? "Finish" : "Continue"}
            </button>
          </>
        )}
      </div>
    </>
  );
}

/* ============================================================
   4 · Flashcards — self-paced review, no scoring
   ============================================================ */
export function Flashcards({ words, onExit }) {
  const [deck, setDeck] = useState(() => shuffle(words));
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);

  const card = deck[idx];

  const go = (delta) => {
    setFlipped(false);
    setIdx((i) => (i + delta + deck.length) % deck.length);
  };
  const reshuffle = () => {
    setDeck(shuffle(words));
    setIdx(0);
    setFlipped(false);
  };

  return (
    <>
      <div className="game-head">
        <div className="qkind">flashcards</div>
        <div className="game-score">
          {idx + 1}/{deck.length}
        </div>
      </div>
      <div className="qarea">
        <button
          className={`flashcard ${flipped ? "flipped" : ""}`}
          onClick={() => setFlipped((f) => !f)}
        >
          {!flipped ? (
            <span className="flash-word">
              {card.key}
              <SpeakButton text={card.key} className="big" />
            </span>
          ) : (
            <span className="flash-meaning">
              <b>{card.g}</b>
              <Html className="notes" html={card.n} />
            </span>
          )}
          <span className="flash-tap">tap to flip</span>
        </button>
      </div>
      <div className="foot flash-foot">
        <button className="btn ghost flash-nav" onClick={() => go(-1)}>
          ‹ prev
        </button>
        <button className="btn ghost flash-nav" onClick={reshuffle}>
          <Icon name="shuffle" />
        </button>
        <button className="btn flash-nav" onClick={() => go(1)}>
          next ›
        </button>
      </div>
    </>
  );
}

/* ---------------- shared end-of-game card ---------------- */
function GameDone({ title, sub, cards, onAgain, onExit }) {
  return (
    <div className="done-screen">
      <div className="trophy">
        <Icon name="trophy" />
      </div>
      <div className="big">{title}</div>
      <div className="sub">{sub}</div>
      <div className="scorecards">
        {cards.map((c) => (
          <div className="scorecard" key={c.k}>
            <div className="k">{c.k}</div>
            <div className="v" style={{ color: c.accent }}>
              {c.v}
            </div>
          </div>
        ))}
      </div>
      <button className="btn" style={{ maxWidth: 320, marginBottom: 12 }} onClick={onAgain}>
        Play again
      </button>
      <button className="btn ghost" style={{ maxWidth: 320 }} onClick={onExit}>
        Back to games
      </button>
    </div>
  );
}
